import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Runs a callback exactly once, when the component first mounts
 * - the callback does not re-run while its component remains mounted or if the component re-mounts
 * - the callback may be async, but this hook will not await it
 */
export function useExactlyOnce(callback: () => void | Promise<void>) {
  const callbackRef = useRef(callback);
  const hasRun = useRef(false);
  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    void callbackRef.current();
  }, []);
}

/**
 * Runs a callback exactly once, when the component first mounts
 * - the callback does not re-run while its component remains mounted or if the component re-mounts
 * - the callback may be async, but this hook will not await it
 * - the callback is run in a useLayoutEffect (before the browser repaints)
 */
export function useExactlyOnceBeforePaint(
  callback: () => void | Promise<void>,
) {
  const callbackRef = useRef(callback);
  const hasRun = useRef(false);
  useLayoutEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    void callbackRef.current();
  }, []);
}
